import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["prefix", "size", "defaultValue", "value", "maxCount", "isPreview", "readOnly", "isDebounce", "renderPreview", "onChange", "hasClear", "className", "style"];
import { Fragment } from "react";
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { findDOMNode } from 'react-dom';
import { CnInput, Overlay, Icon, Balloon, CnInputTextArea } from '@alife/cn-ui';
import classnames from 'classnames';
import { useWillUpdate, useForceUpdate, useDebounceFn } from "./hooks";
import { useI18n } from '@alife/panda-i18n';
import "./style/index.scss";

// !!! 注意 BatchAddCnInputProps 会被 ../index 使用

function BatchAddCnInput(_ref) {
  var _findDOMNode, _innerValueRef$curren;
  var _ref$prefix = _ref.prefix,
    prefix = _ref$prefix === void 0 ? 'cn-next-' : _ref$prefix,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'small' : _ref$size,
    defaultValue = _ref.defaultValue,
    value = _ref.value,
    maxCount = _ref.maxCount,
    _ref$isPreview = _ref.isPreview,
    isPreview = _ref$isPreview === void 0 ? false : _ref$isPreview,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    _ref$isDebounce = _ref.isDebounce,
    isDebounce = _ref$isDebounce === void 0 ? false : _ref$isDebounce,
    renderPreview = _ref.renderPreview,
    onChange = _ref.onChange,
    _ref$hasClear = _ref.hasClear,
    hasClear = _ref$hasClear === void 0 ? true : _ref$hasClear,
    className = _ref.className,
    style = _ref.style,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var $i18n = useI18n();
  var inputRef = useRef(null);
  var _useState = useState(false),
    visible = _useState[0],
    setVisible = _useState[1];
  var innerValueRef = useRef(defaultValue !== null && defaultValue !== void 0 ? defaultValue : value);
  var pasteRef = useRef(null);
  var textareaRef = useRef(null);
  var update = useForceUpdate();
  var handleChange = useCallback(function (val) {
    if (pasteRef.current) {
      var count = pasteRef.current.split(',').length;
      if (maxCount && count > maxCount) return;
    }
    if (val && val.split && maxCount) {
      val = val.split(',').slice(0, maxCount).join(',');
    }
    innerValueRef.current = pasteRef.current || val || '';
    pasteRef.current = null;
    update();
    onChange && onChange(innerValueRef.current);
  }, [innerValueRef, update, onChange, maxCount]);
  var debounceHandleChange = useDebounceFn(handleChange, 100);
  var handlePaste = function handlePaste(event) {
    var _format;
    var clipboardData = event.clipboardData;
    var text = clipboardData.getData('text/plain');
    if (!text.includes('\n')) {
      return;
    }
    // console.log('handlePaste', handlePaste);
    var format = text.replace(/\n/g, ',');
    if (format.includes('\r')) {
      format = format.replace(/\r/g, '');
    }
    if ((_format = format) !== null && _format !== void 0 && _format.endsWith(',')) {
      format = format.substring(0, format.lastIndexOf(','));
    }
    if (innerValueRef.current) {
      var _window$getSelection;
      var selectedValue = (_window$getSelection = window.getSelection()) === null || _window$getSelection === void 0 ? void 0 : _window$getSelection.toString();
      if (selectedValue) {
        var items = innerValueRef.current.split(selectedValue);
        format = "" + items[0] + format + items[1];
      } else {
        format = "" + innerValueRef.current + format;
      }
    }
    console.log('format', format);
    pasteRef.current = format || '';
  };
  useWillUpdate(function () {
    innerValueRef.current = value;
  }, [value]);
  useEffect(function () {
    if (visible) {
      textareaRef.current && textareaRef.current.focus && textareaRef.current.focus(-1, -1);
    }
  }, [visible]);
  if (isPreview) {
    return renderPreview ? renderPreview(innerValueRef.current) : /*#__PURE__*/React.createElement("div", {
      className: prefix + "form-preview"
    }, innerValueRef.current);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CnInput, _extends({
    placeholder: $i18n.get({
      id: 'PleaseEnter',
      dm: '请输入'
    })
  }, rest, {
    className: classnames('cnc-batchadd-input', {
      'cnc-batchadd-hasvalue': !!innerValueRef.current
    }, 'cnc-upper-input', className),
    style: style,
    ref: inputRef,
    size: size,
    value: innerValueRef.current,
    onPaste: handlePaste,
    onChange: function onChange(val, e) {
      e && e.preventDefault();
      e && e.stopPropagation();
      if (isDebounce) {
        innerValueRef.current = val;
        update();
        return debounceHandleChange(val);
      }
      handleChange(val);
    },
    onFocus: function onFocus() {
      return setVisible(false);
    },
    readOnly: readOnly,
    innerAfter: /*#__PURE__*/React.createElement("div", {
      className: "cnc-input-op"
    }, !hasClear || readOnly || rest.disabled ? '' : /*#__PURE__*/React.createElement(Icon, {
      className: "cnc-delete-icon",
      type: "delete-filling",
      size: size,
      onClick: function onClick() {
        pasteRef.current = null;
        handleChange();
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "cnc-border-line"
    }), /*#__PURE__*/React.createElement(Icon, {
      className: "cnc-add-icon mode-" + (readOnly ? 'read-only' : 'normal'),
      type: "add",
      size: size,
      onClick: function onClick() {
        if (!readOnly) {
          setVisible(!rest.disabled);
        }
      }
    }))
  })), /*#__PURE__*/React.createElement(Overlay, {
    visible: visible,
    target: function target() {
      return inputRef.current;
    },
    safeNode: function safeNode() {
      return inputRef.current;
    },
    onRequestClose: function onRequestClose() {
      setVisible(false);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "cnc-batchadd-dropdown " + prefix + size,
    style: {
      // eslint-disable-next-line react/no-find-dom-node
      width: (_findDOMNode = findDOMNode(inputRef === null || inputRef === void 0 ? void 0 : inputRef.current)) === null || _findDOMNode === void 0 ? void 0 : _findDOMNode.offsetWidth
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "cnc-batchadd-dropdown-title"
  }, /*#__PURE__*/React.createElement("span", null, $i18n.get({
    id: 'BatchAdd',
    dm: '批量添加'
  })), /*#__PURE__*/React.createElement("span", null, $i18n.get({
    id: 'LineBreaksBetweenEachPieceOf_162455207',
    dm: '(每条数据之间换行)'
  })), !!maxCount && /*#__PURE__*/React.createElement(Balloon.Tooltip, {
    trigger: /*#__PURE__*/React.createElement(Icon, {
      type: "help",
      size: "small"
    })
  }, $i18n.get({
    id: 'UpToMaxCountDataCanBe_405345824',
    dm: '最多可添加{maxCount}条数据'
  }, {
    maxCount: maxCount
  }))), /*#__PURE__*/React.createElement(CnInputTextArea, {
    ref: textareaRef,
    value: (_innerValueRef$curren = innerValueRef.current) === null || _innerValueRef$curren === void 0 ? void 0 : _innerValueRef$curren.replace(/,/g, '\n'),
    readOnly: readOnly,
    onChange: function onChange(val) {
      var count = val.split('\n').length;
      if (maxCount && count > maxCount) return;
      if (isDebounce) {
        var vals = val.replace(/\n/g, ',');
        innerValueRef.current = vals;
        update();
        return debounceHandleChange(vals);
      }
      handleChange(val.replace(/\n/g, ','));
    }
  }))));
}
export default BatchAddCnInput;